/**
 * Онбординг профиля компании при первом входе (§3.1, D5, p2-company-profile). Если
 * профиль неполон и клиент ещё не пропускал заполнение (см. MeContext.onboarding),
 * CabinetLayout показывает эту модалку: заполнить сейчас (форма реквизитов), пропустить
 * (признак сохраняется на сервере — модалка не показывается навязчиво повторно) или просто
 * закрыть кнопкой-крестиком/Escape без сохранения (замечание РП №2, p2-remarks-2) — тогда
 * ничего не отправляется на сервер, и модалка вернётся при следующем входе, если профиль
 * так и остался неполным. Возврат к заполнению — раздел «Настройки» (там форма реквизитов
 * открыта всегда).
 */
import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { companyApi, type CompanyProfile, type UpdateCompanyProfileInput } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { CompanyProfileForm } from '@/company/company-profile-form'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Icon } from '@/design-system/components/primitives/icon'
import { Alert } from '@/design-system/components/feedback/alert'
import { Dialog, DialogClose, DialogContent } from '@/design-system/components/primitives/dialog'

export interface OnboardingModalProps {
  /** «Пропустить» подтверждён сервером — родитель скрывает модалку и обновляет контекст. */
  onSkip: () => void
  /** Профиль сохранён (полностью или частично) — родитель скрывает модалку и обновляет контекст. */
  onComplete: () => void
  /**
   * Закрытие БЕЗ обращения к серверу (замечание РП №2, p2-remarks-2): кнопка закрытия (X) или
   * Escape. Родитель лишь скрывает модалку локально (уже существующий флаг `onboardingDismissed`
   * в `CabinetLayout`) — онбординг на сервере не отмечается ни пройденным, ни пропущенным,
   * поэтому при следующем маунте кабинета (новый вход/переход) модалка покажется снова, если
   * профиль всё ещё неполон. Отдельного состояния под это заводить не нужно.
   */
  onDismiss: () => void
}

export function OnboardingModal({ onSkip, onComplete, onDismiss }: OnboardingModalProps) {
  const [profile, setProfile] = useState<CompanyProfile | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [skipping, setSkipping] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    companyApi
      .getProfile(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setProfile(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить профиль'))
      })
    return () => controller.abort()
  }, [])

  async function handleSkip() {
    setError(null)
    setSkipping(true)
    try {
      await companyApi.skipOnboarding()
      onSkip()
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось пропустить заполнение'))
    } finally {
      setSkipping(false)
    }
  }

  async function handleSave(input: UpdateCompanyProfileInput): Promise<CompanyProfile> {
    const updated = await companyApi.updateProfile(input)
    onComplete()
    return updated
  }

  return (
    // Заполнить / «Пропустить» / явное закрытие (кнопка-крестик или Escape) — выходы из
    // онбординга (замечание РП №2, p2-remarks-2): модалка больше не «принудительная».
    // Клик вне панели НАМЕРЕННО остаётся заблокированным (onInteractOutside ниже) — случайный
    // клик мимо не должен незаметно сбрасывать начатую форму; для выхода есть явная кнопка и
    // Escape. Фокус-ловушка при этом работает как обычно.
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onDismiss()
      }}
    >
      <DialogContent
        title="Заполнение профиля организации"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <Card
          headerClassName="sticky top-0 z-10 ds-surface rounded-t-12"
          header={
            <div className="flex items-center justify-between gap-2">
              <span>Заполните профиль организации</span>
              <DialogClose asChild>
                <button
                  type="button"
                  aria-label="Закрыть"
                  className="rounded-6 p-1 text-txt-tertiary transition-colors hover:bg-hover hover:text-txt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <Icon as={X} size="sm" />
                </button>
              </DialogClose>
            </div>
          }
        >
          <p className="mb-4 text-txt-secondary">
            Укажите реквизиты компании — они понадобятся для заказов и документов. Это можно сделать
            сейчас или позже, в разделе «Настройки».
          </p>
          {error ? (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          ) : null}
          {!expanded ? (
            <div className="flex gap-3">
              <Button onClick={() => setExpanded(true)}>Заполнить профиль</Button>
              <Button variant="secondary" onClick={() => void handleSkip()} disabled={skipping}>
                {skipping ? 'Секунду…' : 'Пропустить'}
              </Button>
            </div>
          ) : profile ? (
            <CompanyProfileForm
              profile={profile}
              onSave={handleSave}
              hideManagerFields
              onDaDataLookup={companyApi.dadataLookup}
            />
          ) : (
            <p className="text-txt-secondary">Загрузка…</p>
          )}
        </Card>
      </DialogContent>
    </Dialog>
  )
}
